import { ReactNode, useContext } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { useTranslation } from 'libs/i18n'
import { NavLink } from 'libs/router'
import { ContextMenu, Currency, TableImage } from 'shared/components'
import { Badge, BooleanBadge } from 'shared/atoms'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import { BookingStatus } from 'features/bookings'
import type { CardTableColumn } from 'styles/table'
import type { Option } from 'shared/components/ContextMenu/types'
import type { ProductItem } from 'features/products/types/products.types'
import { useProductPath } from './useProductPath'

type UseColumnsProps = {
	getMenuOptions: (product: ProductItem) => Option[]
}

const MAX_TAGS_COUNT = 2

const cell = (render: (product: ProductItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as ProductItem)

const numeric = 'block whitespace-nowrap text-right tabular-nums'
const muted = 'block whitespace-nowrap text-right tabular-nums text-muted-foreground'
const chip = 'rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground'
const dash = <span className="text-muted-foreground">—</span>

const text = (value?: string | null) => value || dash

export const useColumns = ({ getMenuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const settings = storeCtx?.storeSettings
	const productPath = useProductPath()

	return [
		{
			label: t('Product'),
			width: 'minmax(230px, 1.4fr)',
			renderCell: cell((item) => (
				<div className="flex items-center gap-3">
					<TableImage src={item.image_thumbnail} />
					<div className="min-w-0 flex-1">
						<NavLink
							className="block truncate text-[13px] font-semibold text-primary hover:underline"
							to={productPath(item.id)}>
							{item.name}
						</NavLink>
						<span className="block truncate text-[11px] text-muted-foreground">{item.sku}</span>
					</div>
				</div>
			)),
			select: true,
			pinLeft: true
		},
		...(isStaff
			? [
					{
						label: t('Booking status'),
						width: '130px',
						renderCell: cell((item) =>
							item.booking?.status ? <BookingStatus status={item.booking.status} /> : dash
						)
					},
					{
						label: t('Owner'),
						width: 'minmax(160px, 1fr)',
						renderCell: cell((item) => (
							<span className="block leading-snug">
								<span className="block truncate font-medium">{item.owner?.name}</span>
								<span className="block truncate text-[11px] text-muted-foreground">
									{item.owner?.email}
								</span>
							</span>
						))
					}
				]
			: []),
		...(settings?.categories_enabled
			? [
					{
						label: t('Category'),
						width: 'minmax(110px, 1fr)',
						renderCell: cell((item) => text(item.category?.name))
					}
				]
			: []),
		...(settings?.tags_enabled
			? [
					{
						label: t('Tags'),
						width: 'minmax(120px, 1fr)',
						renderCell: cell((item) =>
							item.tags?.length ? (
								<span className="flex flex-wrap gap-1">
									{item.tags.slice(0, MAX_TAGS_COUNT).map((tag) => (
										<span key={tag} className={chip}>
											{tag}
										</span>
									))}
									{item.tags.length > MAX_TAGS_COUNT && (
										<span className={chip}>+{item.tags.length - MAX_TAGS_COUNT}</span>
									)}
								</span>
							) : (
								dash
							)
						)
					}
				]
			: []),
		...(settings?.product_fabrics_enabled
			? [
					{
						label: t('Fabric'),
						width: 'minmax(100px, 1fr)',
						renderCell: cell((item) => text(item.product_fabric?.name))
					}
				]
			: []),
		...(settings?.product_conditions_enabled
			? [
					{
						label: t('Condition'),
						width: 'minmax(100px, 1fr)',
						renderCell: cell((item) => text(item.product_condition?.name))
					}
				]
			: []),
		...(settings?.product_genders_enabled
			? [
					{
						label: t('Gender'),
						width: 'minmax(100px, 1fr)',
						renderCell: cell((item) => text(item.product_gender?.name))
					}
				]
			: []),
		...(settings?.product_types_enabled
			? [
					{
						label: t('Type'),
						width: 'minmax(100px, 1fr)',
						renderCell: cell((item) => text(item.product_type?.name))
					}
				]
			: []),
		...(settings?.supplier_sku_barcode_enabled
			? [
					{
						label: t('Barcode'),
						width: 'minmax(110px, 1fr)',
						renderCell: cell((item) => text(item.supplier_barcode))
					},
					{
						label: t('SKU'),
						width: 'minmax(100px, 1fr)',
						renderCell: cell((item) => text(item.supplier_sku))
					}
				]
			: []),
		{
			label: t('Size'),
			width: '80px',
			align: 'right',
			renderCell: cell((item) => <span className={numeric}>{item.size || dash}</span>)
		},
		{
			label: t('Price'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.price} />)
		},
		...(settings?.vat_enabled
			? [
					{
						label: t('VAT'),
						width: '70px',
						align: 'right' as const,
						renderCell: cell((item) => (
							<span className={muted}>{item.vat_rate ? `${item.vat_rate}%` : dash}</span>
						))
					}
				]
			: []),
		{
			label: t('Quantity'),
			width: '90px',
			align: 'right',
			renderCell: cell((item) => <span className={numeric}>{item.quantity}</span>)
		},
		{
			label: t('Area'),
			width: 'minmax(150px, 1fr)',
			renderCell: cell((item) =>
				item.booking?.id ? (
					<span className="block leading-snug">
						<span className="block whitespace-nowrap font-medium">
							{item.booking.booth?.booth_type?.name}
							{item.booking.booth?.number ? ` #${item.booking.booth.number}` : ''}
						</span>
						<span className="block whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
							#{item.booking.reference} {item.booking.start_date}
						</span>
					</span>
				) : (
					dash
				)
			)
		},
		{
			label: t('Sold'),
			width: '70px',
			align: 'right',
			renderCell: cell((item) => <span className={numeric}>{item.sum_sold_quantity}</span>)
		},
		{
			label: t('Discount'),
			width: '100px',
			align: 'right',
			renderCell: cell((item) => <Currency className={muted} price={item.sum_sold_discount} />)
		},
		{
			label: t('Total sales'),
			width: '110px',
			align: 'right',
			renderCell: cell((item) => <Currency className={numeric} price={item.sum_sold_total} />)
		},
		{
			label: t('Status'),
			width: '130px',
			renderCell: cell((item) => (
				<Badge uppercase variant={item.published ? 'success' : 'danger'}>
					{item.published ? t('Published') : t('Unpublished')}
				</Badge>
			))
		},
		...(isStaff
			? [
					{
						label: t('Regla synced'),
						width: '110px',
						align: 'center' as const,
						renderCell: cell((item) => (
							<span className="block text-center">
								<BooleanBadge value={!!item.regla_synced} label={t('Regla synced')} />
							</span>
						))
					}
				]
			: []),
		{
			label: t('Printed'),
			width: '80px',
			align: 'center',
			renderCell: cell((item) => (
				<span className="block text-center">
					<BooleanBadge value={!!item.print_count} label={t('Printed')} />
				</span>
			))
		},
		...(settings?.product_confirm_enabled
			? [
					{
						label: t('Confirmed'),
						width: '100px',
						align: 'center' as const,
						renderCell: cell((item) => (
							<span className="block text-center">
								{item.confirmed != null ? (
									<BooleanBadge value={item.confirmed} label={t('Confirmed')} />
								) : (
									dash
								)}
							</span>
						))
					}
				]
			: []),
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={getMenuOptions(item)}
					label={t('Actions for {{ name }}', { name: item.name })}
				/>
			))
		}
	]
}
